import {Injectable, signal} from '@angular/core';
import {UpdateColumnCheckboxParams, ColumnCheckboxParams} from "../../../types/table-column-checkbox.types";
import {defaultColumnCheckboxParams} from "../../../models/default-column-checkbox-params";

@Injectable({
  providedIn: 'root'
})
export class TableColumnCheckboxService {
  private _columnCheckboxState = signal<ColumnCheckboxParams>(defaultColumnCheckboxParams);
  public columnCheckboxState = this._columnCheckboxState.asReadonly();

  public setColumnCheckboxState(ids: Array<number>) {
    this._columnCheckboxState.set({
      completed: false,
      subtasks: ids.map(id => ({ id, completed: false })),
    });
  }

  public updateColumnCheckboxState({ completed, id }: UpdateColumnCheckboxParams): void {
    this._columnCheckboxState.update(currentState => {
      const newState = this.deepCloneState(currentState);

      id ? this.handleTaskWithId(newState, id, completed)
        : this.handleMainCheckbox(newState, completed);

      this.updateMainCheckboxState(newState);

      return newState;
    });
  }

  public getColumnCheckboxStateIds(): Array<number> {
    return this.columnCheckboxState().subtasks
      .filter(task => task.completed)
      .map(task => task.id)
      .filter((id): id is number => id !== undefined);
  }

  private deepCloneState(state: ColumnCheckboxParams): ColumnCheckboxParams {
    return JSON.parse(JSON.stringify(state));
  }

  private handleMainCheckbox(state: ColumnCheckboxParams, completed: boolean): void {
    state.completed = completed;
    state.subtasks?.forEach(task => task.completed = completed);
  }

  private handleTaskWithId(state: ColumnCheckboxParams, id: number, completed: boolean): void {
    const existingTask = this.findTaskById(state.subtasks, id);

    existingTask ? this.updateExistingTask(existingTask, completed)
      : this.addNewTask(state.subtasks, id, completed);
  }

  private findTaskById(tasks: UpdateColumnCheckboxParams[], id: number): UpdateColumnCheckboxParams | undefined {
    return tasks.find(task => task.id === id);
  }

  private updateExistingTask(task: UpdateColumnCheckboxParams, completed: boolean): void {
    task.completed = completed;
  }

  private addNewTask(tasks: UpdateColumnCheckboxParams[], id: number, completed: boolean): void {
    tasks.push({ id, completed });
  }

  private updateMainCheckboxState(state: ColumnCheckboxParams): void {
    state.completed = state.subtasks.every(task => task.completed);
  }
}
